"use client";
import { useRef, useState } from "react";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { AlertCircle, FileAudio, FileText, Loader2, Pause, Play, X } from "lucide-react";
import { Button } from "@/app/components/ui";
import { useStateHistoryChatPage } from "@/app/store/chat";
import { ATTACHMENT_PROCESSING_STATUSES } from "@/app/lib/constants";
import { cn } from "@/app/lib/utils";

const formatFileSize = (bytes: number) => {
   if (bytes < 1024) return `${bytes} B`;
   if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
   return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDurationLimit = (seconds: number) =>
   `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;

const AudioPlayback = ({ src }: { src: string }) => {
   const translation = useTranslations("Home");
   const audioRef = useRef<HTMLAudioElement>(null);
   const [isPlaying, setIsPlaying] = useState(false);

   const togglePlayback = () => {
      const audio = audioRef.current;
      if (!audio) return;

      if (audio.paused) {
         void audio.play();
      } else {
         audio.pause();
      }
   };

   return (
      <>
         <audio
            ref={audioRef}
            src={src}
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            onEnded={() => setIsPlaying(false)}
         />
         <Button
            variant="ghost"
            size="icon"
            onClick={togglePlayback}
            aria-label={translation(isPlaying ? "pauseAudio" : "playAudio")}
            title={translation(isPlaying ? "pauseAudio" : "playAudio")}
            className="h-8 w-8 rounded-full text-foreground hover:bg-primary/10 hover:text-primary"
         >
            {isPlaying ? <Pause size={16} /> : <Play size={16} />}
         </Button>
      </>
   );
};

export const ChatInputAttachments = ({ isSending }: { isSending: boolean }) => {
   const translation = useTranslations("Home");
   const { pendingAttachments, removePendingAttachment } = useStateHistoryChatPage();
   const processingLabels: Record<string, string> = {
      optimizing: translation("attachmentOptimizing"),
      uploading: translation("attachmentUploading"),
      queued: translation("attachmentQueued"),
      scanning: translation("attachmentScanning"),
      validating: translation("attachmentValidating"),
      extracting: translation("attachmentExtracting")
   };

   // "File is too large" on its own leaves the user guessing how much to trim.
   const describeFailure = (
      error?: string,
      maxSizeMB?: number,
      maxDurationSeconds?: number
   ) => {
      if (
         error &&
         ["tooLarge", "imageSourceTooLarge", "attachmentsTotalTooLarge"].includes(error)
      ) {
         return translation(error, { maxSizeMB: maxSizeMB || 0 });
      }
      if (error === "audioTooLong") {
         return translation(error, {
            maxDuration: formatDurationLimit(maxDurationSeconds || 0)
         });
      }
      return translation(error || "uploadFailed");
   };

   // Hidden rather than cleared: the sent message already shows these files, so leaving
   // the row up duplicates them on screen until the response arrives. The staged uploads
   // stay in the store, so a request that fails before `clearPendingAttachments` brings
   // the row back with everything still attached.
   if (isSending || !pendingAttachments.length) return null;

   return (
      <div className="flex flex-wrap gap-2 px-4 pt-3">
         {pendingAttachments.map((attachment) => {
            const isImage = Boolean(attachment.previewUrl);
            const isAudio = attachment.mimetype.startsWith("audio/");
            const hasFailed = attachment.status === "error";
            const isProcessing = ATTACHMENT_PROCESSING_STATUSES.includes(
               attachment.status as (typeof ATTACHMENT_PROCESSING_STATUSES)[number]
            );

            if (isAudio) {
               return (
                  <div
                     key={attachment.localId}
                     className={cn(
                        "flex h-12 items-center gap-1 rounded-xl border border-border bg-background/60 px-2",
                        isProcessing && "border-primary/40 bg-primary/5",
                        hasFailed && "border-destructive/50 bg-destructive/10"
                     )}
                  >
                     <span
                        className="relative flex h-8 w-8 items-center justify-center overflow-hidden rounded-lg bg-primary/10 text-primary"
                        aria-label={
                           isProcessing ? translation("audioProcessing") : undefined
                        }
                        title={isProcessing ? translation("audioProcessing") : undefined}
                     >
                        <FileAudio
                           size={16}
                           className={cn(isProcessing && "opacity-25")}
                        />
                        {isProcessing && (
                           <span className="absolute inset-0 flex items-center justify-center bg-background/70 backdrop-blur-[1px]">
                              <Loader2 size={16} className="animate-spin text-primary" />
                           </span>
                        )}
                     </span>
                     {attachment.playbackUrl && (
                        <AudioPlayback src={attachment.playbackUrl} />
                     )}
                     <span className="flex min-w-0 flex-col">
                        <span className="max-w-[10rem] truncate text-xs font-medium text-foreground">
                           {attachment.name}
                        </span>
                        <span
                           className={cn(
                              "text-[10px] text-muted-foreground",
                              hasFailed && "text-destructive"
                           )}
                        >
                           {hasFailed
                              ? describeFailure(
                                   attachment.error,
                                   attachment.maxSizeMB,
                                   attachment.maxDurationSeconds
                                )
                              : isProcessing
                                ? processingLabels[attachment.status]
                                : formatFileSize(attachment.size)}
                        </span>
                     </span>
                     {hasFailed && <AlertCircle size={14} className="text-destructive" />}
                     <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => removePendingAttachment(attachment.localId)}
                        aria-label={translation("removeAttachment")}
                        className="h-8 w-8 rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                     >
                        <X size={14} />
                     </Button>
                  </div>
               );
            }

            return (
               <div
                  key={attachment.localId}
                  className={cn(
                     "group relative flex items-center gap-2 rounded-xl border border-border bg-background/60 py-2 pl-2 pr-9",
                     hasFailed && "border-destructive/50 bg-destructive/10"
                  )}
               >
                  {isImage ? (
                     <Image
                        src={attachment.previewUrl!}
                        alt={attachment.name}
                        width={32}
                        height={32}
                        unoptimized
                        className="h-8 w-8 rounded-lg object-cover"
                     />
                  ) : (
                     <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                        <FileText size={16} />
                     </span>
                  )}

                  <span className="flex min-w-0 flex-col">
                     <span className="max-w-[10rem] truncate text-xs font-medium text-foreground">
                        {attachment.name}
                     </span>
                     <span
                        className={cn(
                           "text-[10px] text-muted-foreground",
                           hasFailed && "text-destructive"
                        )}
                     >
                        {hasFailed
                           ? describeFailure(
                                attachment.error,
                                attachment.maxSizeMB,
                                attachment.maxDurationSeconds
                             )
                           : isProcessing
                             ? processingLabels[attachment.status]
                             : formatFileSize(attachment.size)}
                     </span>
                  </span>

                  {isProcessing && (
                     <Loader2 size={14} className="animate-spin text-muted-foreground" />
                  )}
                  {hasFailed && <AlertCircle size={14} className="text-destructive" />}

                  <Button
                     variant="ghost"
                     size="icon"
                     onClick={() => removePendingAttachment(attachment.localId)}
                     aria-label={translation("removeAttachment")}
                     className="absolute right-1 top-1/2 h-6 w-6 -translate-y-1/2 rounded-lg text-muted-foreground hover:border-primary/40 hover:bg-primary/10 hover:text-primary"
                  >
                     <X size={12} />
                  </Button>
               </div>
            );
         })}
      </div>
   );
};
